import { Badge, Button, Callout, Flex, Text } from '@radix-ui/themes'
import { ComponentProps, useState } from 'react'
import { CheckCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { PamPlayerAccountSummary, PamPlayerStatus } from '@Generated/back-office-client/src'
import { ReasonModal } from '@Components/common/reason-modal'
import { formatDateToLongMonthDayYearString } from '@Components/util/utils'
import { getAccountBannerDisplay, getAccountStatusDisplay } from './pam-player-status-display'
import { readAccountStatusError } from './pam-account-status-errors'
import { PamAccountAction } from './types'

type DialogCopy = {
  trigger: string
  title: string
  body: string
  confirm: string
  color: ComponentProps<typeof Button>['color']
}

const DIALOG_COPY: Record<PamAccountAction, DialogCopy> = {
  [PamAccountAction.REACTIVATE]: {
    trigger: 'Reactivate',
    title: 'Reactivate account',
    body:
      'Reactivating lets this player sign in again straight away. Check that the reason for ' +
      'the closure no longer applies, and if the player closed the account themselves, that ' +
      "they've asked to reopen it. Their verification status is not changed.",
    confirm: 'Reactivate account',
    color: 'amber',
  },
  [PamAccountAction.DEACTIVATE]: {
    trigger: 'Deactivate',
    title: 'Deactivate account',
    body:
      "The player will be signed out on every device and won't be able to sign in until the " +
      'account is reactivated.',
    confirm: 'Deactivate account',
    color: 'red',
  },
}

// Deactivate is refused for an already-deactivated account; use Reactivate instead.
const getAvailableAction = (status: string): PamAccountAction => {
  if (status === PamPlayerStatus.deactivated) {
    return PamAccountAction.REACTIVATE
  }
  return PamAccountAction.DEACTIVATE
}

export type PamAccountStatusBannerProps = {
  account: PamPlayerAccountSummary
  canWrite: boolean
  onSubmit: (action: PamAccountAction, reason: string) => Promise<void>
}

/**
 * Player-details account banner: the account status, when and why it last changed, and the
 * reactivate or deactivate action behind a confirmation.
 */
export const PamAccountStatusBanner = ({
  account,
  canWrite,
  onSubmit,
}: PamAccountStatusBannerProps) => {
  const [openAction, setOpenAction] = useState<PamAccountAction | null>(null)
  const [isPending, setIsPending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const banner = getAccountBannerDisplay(account.status)
  const accountDisplay = getAccountStatusDisplay(account.status)
  const Icon = banner.attention ? ExclamationTriangleIcon : CheckCircledIcon
  const action = canWrite ? getAvailableAction(account.status) : null
  const showChange = account.status !== PamPlayerStatus.active

  const openDialog = (next: PamAccountAction) => {
    setErrorMessage(null)
    setOpenAction(next)
  }

  const submit = async (reason: string) => {
    if (openAction === null) {
      return
    }
    setIsPending(true)
    setErrorMessage(null)
    try {
      await onSubmit(openAction, reason)
      setOpenAction(null)
    } catch (e) {
      setErrorMessage(await readAccountStatusError(e))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <>
      <Callout.Root
        color={banner.color}
        variant="soft"
        style={{ display: 'flex', alignItems: 'center' }}>
        <Flex align="center" justify="between" gap="3" width="100%">
          <Flex direction="column" gap="1">
            <Flex align="center" gap="2">
              <Icon />
              <Text size="2" weight="bold">
                Account
              </Text>
              <Badge color={accountDisplay.color} variant="soft">
                {accountDisplay.label}
              </Badge>
              {showChange && (
                <Text size="2">
                  since {formatDateToLongMonthDayYearString(account.statusChangedAt)}
                </Text>
              )}
            </Flex>
            {showChange && account.statusReason && (
              <Text size="2">Reason: {account.statusReason}</Text>
            )}
          </Flex>
          {action !== null && (
            <Button color={DIALOG_COPY[action].color} onClick={() => openDialog(action)}>
              {DIALOG_COPY[action].trigger}
            </Button>
          )}
        </Flex>
      </Callout.Root>

      {openAction !== null && (
        <ReasonModal
          title={DIALOG_COPY[openAction].title}
          description={DIALOG_COPY[openAction].body}
          confirmLabel={DIALOG_COPY[openAction].confirm}
          confirmColor={DIALOG_COPY[openAction].color}
          isPending={isPending}
          errorMessage={errorMessage}
          onCancel={() => setOpenAction(null)}
          onConfirm={submit}>
          <Callout.Root color={DIALOG_COPY[openAction].color} variant="soft">
            <Callout.Text>{DIALOG_COPY[openAction].body}</Callout.Text>
          </Callout.Root>
        </ReasonModal>
      )}
    </>
  )
}
